import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {AppRoutesPrefixesEnum} from '../../../../components/appRoutesPrefixes';
import {findPanelSequence} from '../../../../services/panels';
import {buildPanelCancelToken} from '../../../../services/panelsCancellers';
import {ThunkAction} from '../../../appThunk';
import {getHasNewContactsManagerV0} from '../../../featureFlags/featureFlagsSelectors';
import {setPanelAction} from '../../panelsActions';
import {getPanels} from '../../panelsSelectors';
import {ContactsManagerPanelId, PanelTypesEnum} from '../../panelStateBase';
import {ContactsManagerFileAction} from './file/contactsManagerFileStateActions';
import {
  ContactsManagerParameters,
  ContactsManagerParametersTypesEnum,
} from './parameters/contactsManagerParameters';
import {
  contactsManagerNavigatedAction,
  ContactsManagerRoutesAction,
} from './routing/contactsManagerRoutingStateActions';
import {ContactsManagerSearchAction} from './search/contactsManagerSearchActions';
import {ContactsManagerSidePanelAction} from './sidePanel/contactsManagerSidePanelActions';
import {ContactsManagerTableAction} from './table/contactsManagerTableActions';

/*
 * Action union type.
 */

export type ContactsManagerPanelAction =
  | ContactsManagerSidePanelAction
  | ContactsManagerTableAction
  | ContactsManagerRoutesAction
  | ContactsManagerFileAction
  | ContactsManagerSearchAction;

/*
 * Action creators.
 */

export function navigateContactsManagerAction(
  panelId: ContactsManagerPanelId,
  parameters: ContactsManagerParameters | undefined,
): ThunkAction {
  return (dispatch, getState) => {
    // Find the contacts manager panel with this ID.
    const panel = getPanels(getState())[panelId];
    if (panel && panel.type !== PanelTypesEnum.CONTACTS_MANAGER) {
      throw new Error('Wrong panel type.');
    }

    // If the panel exists and a location was provided, update it.
    if (panel && parameters) {
      dispatch(contactsManagerNavigatedAction(panelId, parameters));
      return;
    }

    // If the panel exists and no location was provided, we're done.
    if (panel) {
      return;
    }

    // Otherwise, create the panel.
    const [cancelTokenId] = buildPanelCancelToken(panelId);
    const hasNewContactsManagerV0 = getHasNewContactsManagerV0(getState());

    dispatch(
      setPanelAction({
        id: panelId,
        type: PanelTypesEnum.CONTACTS_MANAGER,
        status: AsyncStatusesEnum.LOADING,
        cancelTokenId,
        sequence: findPanelSequence(),
        parameters: parameters ?? buildDefaultParameters(hasNewContactsManagerV0),
        sidePanelState: undefined,
        tableState: undefined,
        fileState: undefined,
      }),
    );
  };
}

/*
 * Helpers.
 */

function buildDefaultParameters(hasNewContactsManagerV0: boolean): ContactsManagerParameters {
  if (hasNewContactsManagerV0) {
    return {
      type: ContactsManagerParametersTypesEnum.COMPANY_CONTACTS,
      location: {
        pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_CONTACTS}/global`,
      },
    };
  }
  return {
    type: ContactsManagerParametersTypesEnum.ACCOUNTS,
    location: {
      pathname: `${AppRoutesPrefixesEnum.CONTACTS_MANAGER_ACCOUNTS}`,
    },
  };
}
